import React, { useState, useEffect } from 'react';
import CopyButton from './CopyButton';

console.log('🔄 TEAM-MANAGEMENT: Component loaded');

const TeamManagement = ({ adminService }) => {
  console.log('🔍 TEAM-MANAGEMENT: Rendering');

  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 TEAM-MANAGEMENT: Loading team data');

      // Load pending requests
      const pendingResp = await adminService.getPendingRequests();
      console.log(`✓ Loaded ${pendingResp.total} pending requests`);
      setPendingRequests(pendingResp.pending_requests);

      // Load active users
      const usersResp = await adminService.getUsers({ status: 'active' });
      console.log(`✓ Loaded ${usersResp.total} active users`);
      setActiveUsers(usersResp.users);

    } catch (err) {
      console.error('❌ TEAM-MANAGEMENT: Failed to load team data:', err);
      setError(err.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setActionLoading(`approve-${userId}`);

    try {
      console.log(`🔍 TEAM-MANAGEMENT: Approving user ${userId}`);
      await adminService.approveUser(userId, { role: 'stytch_member' });
      console.log('✓ User approved');

      // Reload data
      await loadTeamData();
    } catch (err) {
      console.error('❌ TEAM-MANAGEMENT: Approve failed:', err);
      setError(err.message || 'Failed to approve user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId) => {
    setActionLoading(`reject-${userId}`);

    try {
      console.log(`🔍 TEAM-MANAGEMENT: Rejecting user ${userId}`);
      await adminService.rejectUser(userId);
      console.log('✓ User rejected');

      // Reload data
      await loadTeamData();
    } catch (err) {
      console.error('❌ TEAM-MANAGEMENT: Reject failed:', err);
      setError(err.message || 'Failed to reject user');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p className="text-text-muted">Loading team data...</p>
      </div>
    );
  }

  const loginUrl = window.location.origin;

  return (
    <div className="space-y-6">
      {/* Add Members Banner */}
      <div className="p-4 bg-blue-500/20 border border-blue-500/40 rounded text-sm">
        <p className="text-blue-400 font-medium mb-1">How to add team members</p>
        <p className="text-text-secondary">
          Share your login page with your team:{' '}
          <span className="inline-flex items-center gap-1">
            <span className="text-text-primary font-mono">{loginUrl}</span>
            <CopyButton text={loginUrl} size="xs" label="Copy login URL" />
          </span>
        </p>
        <p className="text-text-muted mt-1">
          Team members sign in with their company email. Their request will appear in the table below for your approval.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Pending Requests Section */}
      {pendingRequests.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            Pending Requests ({pendingRequests.length})
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Phone</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Requested</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((request) => (
                  <tr key={request.user_id} className="border-b border-border-subtle/50 hover:bg-background-secondary/50">
                    <td className="py-3 px-4 text-text-primary">
                      {request.first_name} {request.last_name}
                    </td>
                    <td className="py-3 px-4 text-text-secondary text-sm">{request.email}</td>
                    <td className="py-3 px-4 text-text-secondary text-sm">{request.phone || '-'}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                        Pending
                      </span>
                    </td>
                    <td className="py-3 px-4 text-text-muted text-sm">
                      {request.requested_days_ago} day{request.requested_days_ago !== 1 ? 's' : ''} ago
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(request.user_id)}
                          disabled={actionLoading !== null}
                          className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-sm hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading === `approve-${request.user_id}` ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(request.user_id)}
                          disabled={actionLoading !== null}
                          className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {actionLoading === `reject-${request.user_id}` ? '...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active Members Section */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Active Members ({activeUsers.length})
        </h3>

        {activeUsers.length === 0 ? (
          <p className="text-text-muted text-sm">No active members yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Phone</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Last Active</th>
                  <th className="text-left py-3 px-4 text-text-secondary text-sm font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers.map((user) => (
                  <tr key={user.user_id} className="border-b border-border-subtle/50 hover:bg-background-secondary/50">
                    <td className="py-3 px-4 text-text-primary">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="py-3 px-4 text-text-secondary text-sm">{user.email}</td>
                    <td className="py-3 px-4 text-text-secondary text-sm">{user.phone || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.role?.includes('admin')
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {user.role?.includes('admin') ? 'Admin' : 'Active'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-text-muted text-sm">
                      {user.last_active_at
                        ? new Date(user.last_active_at * 1000).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="py-3 px-4 text-text-muted text-sm">
                      -
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Empty State */}
      {pendingRequests.length === 0 && activeUsers.length === 0 && (
        <div className="text-center p-6">
          <p className="text-text-muted">No team members or pending requests.</p>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
